import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FileText, ChevronRight } from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] }
};

// Helper to render text with **bold** markers
const renderTextWithBold = (text: string) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="text-white font-semibold">{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

const sections = [
  {
    id: 'acceptance',
    title: '1. Acceptance of Terms',
    content: `By accessing or using Ulearning ("Platform"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, you may not access the Platform.

These Terms apply to all users, including students, instructors, and visitors. Additional terms may apply to specific features or services.`,
  },
  {
    id: 'account',
    title: '2. Account Registration',
    content: `To access certain features, you must create an account. You agree to:
• Provide accurate and complete information
• Maintain the security of your password
• Accept responsibility for all activities under your account
• Notify us immediately of any unauthorized access
• Be at least 16 years old to create an account

We reserve the right to suspend or terminate accounts that violate these Terms.`,
  },
  {
    id: 'use',
    title: '3. Acceptable Use',
    content: `You agree NOT to:
• Violate any applicable laws or regulations
• Infringe on intellectual property rights
• Share account credentials with others
• Use the Platform for unauthorized commercial purposes
• Attempt to gain unauthorized access to our systems
• Upload malicious code or interfere with Platform operations
• Harass, abuse, or harm other users
• Submit false or misleading information
• Scrape or collect user data without permission`,
  },
  {
    id: 'content',
    title: '4. User Content',
    content: `You retain ownership of content you submit. By posting content, you grant Ulearning a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, and display that content in connection with our services.

You are responsible for your content and must not post anything that:
• Infringes third-party intellectual property rights
• Contains illegal, harmful, or offensive material
• Violates privacy rights or contains personal data without consent
• Is false, misleading, or deceptive`,
  },
  {
    id: 'courses',
    title: '5. Courses and Enrollment',
    content: `**For Students:**
• Course access is granted upon successful payment
• Enrolled courses remain accessible per the stated terms
• Certificates are issued upon meeting completion requirements
• Course content may be updated without prior notice

**For Instructors:**
• You must own or have rights to all content you upload
• Pricing and revenue share are as specified in instructor agreements
• You are responsible for course quality and accuracy
• We may remove courses that violate our guidelines`,
  },
  {
    id: 'payments',
    title: '6. Payments and Refunds',
    content: `**Payments:**
• All prices are in USD unless otherwise stated
• Payment processing is handled by Stripe
• Subscription renewals are automatic unless cancelled

**Refunds:**
• 30-day money-back guarantee for most courses
• Refunds are processed to the original payment method
• Refund eligibility may be limited for substantially consumed content
• Subscription refunds are prorated based on usage`,
  },
  {
    id: 'ip',
    title: '7. Intellectual Property',
    content: `The Platform and its original content, features, and functionality are owned by Ulearning and are protected by copyright, trademark, and other intellectual property laws.

Our trademarks and trade dress may not be used without our prior written permission. Course content is owned by respective instructors or Ulearning.`,
  },
  {
    id: 'disclaimer',
    title: '8. Disclaimers',
    content: `THE PLATFORM IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.

We do not guarantee:
• Uninterrupted or error-free service
• Accuracy or reliability of any content
• That the Platform will meet your requirements
• Results from using courses or materials`,
  },
  {
    id: 'liability',
    title: '9. Limitation of Liability',
    content: `TO THE MAXIMUM EXTENT PERMITTED BY LAW, ULEARNING SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES.

Our total liability for any claims arising from your use of the Platform shall not exceed the amount you paid us in the twelve (12) months preceding the claim.`,
  },
  {
    id: 'indemnification',
    title: '10. Indemnification',
    content: `You agree to indemnify, defend, and hold harmless Ulearning, its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses arising from:
• Your use of the Platform
• Your violation of these Terms
• Your violation of any third-party rights
• Content you submit to the Platform`,
  },
  {
    id: 'termination',
    title: '11. Termination',
    content: `We may terminate or suspend your account immediately, without prior notice, for conduct that we believe violates these Terms or is harmful to other users, us, or third parties, or for any other reason.

Upon termination:
• Your right to use the Platform ceases immediately
• Provisions that should survive termination will remain in effect
• We are not liable for any termination of your access`,
  },
  {
    id: 'governing',
    title: '12. Governing Law',
    content: `These Terms shall be governed by the laws of the State of Ohio, United States, without regard to conflict of law principles.

Any disputes arising from these Terms or your use of the Platform shall be resolved exclusively in the state or federal courts located in Columbus, Ohio.`,
  },
  {
    id: 'changes',
    title: '13. Changes to Terms',
    content: `We reserve the right to modify these Terms at any time. Material changes will be notified via email or Platform notice at least 30 days before taking effect.

Your continued use of the Platform after changes become effective constitutes acceptance of the modified Terms.`,
  },
  {
    id: 'contact',
    title: '14. Contact Information',
    content: `For questions about these Terms, please contact us:

Ulearning
Columbus, Ohio, United States
Email: legal@ulearning.com
Support: support@ulearning.com`,
  },
];

export default function TermsOfServicePage() {
  const lastUpdated = 'January 7, 2026';

  return (
    <div className="bg-void min-h-screen">
      {/* Hero Section */}
      <section className="relative py-16 lg:py-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-1/4 w-[600px] h-[400px] bg-slate/15 rounded-full blur-[120px]" />
        </div>

        <div className="container-luxury relative">
          <motion.div
            initial="initial"
            animate="animate"
            variants={fadeUp}
            className="max-w-3xl mx-auto"
          >
            <div className="mb-6 flex items-center gap-2 text-white/40 text-sm">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-white">Terms of Service</span>
            </div>

            <div className="flex items-center gap-4 mb-6">
              <div className="icon-slate">
                <FileText className="w-5 h-5 text-slate-light" />
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-white">
                Terms of Service
              </h1>
            </div>

            <p className="text-white/50">
              Last updated: {lastUpdated}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="pb-10">
        <div className="container-luxury">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="card"
            >
              <h2 className="font-semibold text-white mb-4">Table of Contents</h2>
              <nav className="grid sm:grid-cols-2 gap-2">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="text-white/60 hover:text-white text-sm transition-colors flex items-center gap-2"
                  >
                    <ChevronRight className="w-3 h-3" />
                    {section.title}
                  </a>
                ))}
              </nav>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="pb-20">
        <div className="container-luxury">
          <div className="max-w-3xl mx-auto space-y-12">
            {sections.map((section, index) => (
              <motion.div
                key={section.id}
                id={section.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ delay: index * 0.05 }}
                className="scroll-mt-24"
              >
                <h2 className="text-xl font-bold text-white mb-4 pb-2 border-b border-white/10">
                  {section.title}
                </h2>
                <div className="text-white/60 leading-relaxed whitespace-pre-line">
                  {section.content.split('\n').map((paragraph, i) => {
                    // Check if line is a header (starts and ends with **)
                    if (paragraph.startsWith('**') && paragraph.endsWith('**') && paragraph.indexOf('**', 2) === paragraph.length - 2) {
                      return (
                        <h3 key={i} className="font-semibold text-white mt-4 mb-2">
                          {paragraph.slice(2, -2)}
                        </h3>
                      );
                    }
                    // Check if line starts with ** (subheading like **For Students:** text)
                    if (paragraph.startsWith('**')) {
                      return (
                        <p key={i} className="mb-2">
                          {renderTextWithBold(paragraph)}
                        </p>
                      );
                    }
                    if (paragraph.startsWith('•')) {
                      return (
                        <p key={i} className="ml-4 mb-1">
                          {renderTextWithBold(paragraph)}
                        </p>
                      );
                    }
                    return paragraph ? (
                      <p key={i} className="mb-3">
                        {renderTextWithBold(paragraph)}
                      </p>
                    ) : null;
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 border-t border-white/[0.06]">
        <div className="container-luxury">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-white/50 mb-4">
              Have questions about our terms?
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-secondary">
                Contact Us
              </Link>
              <Link to="/privacy" className="btn-ghost">
                View Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

